import { useCallback, type FC } from "react";
import { WorkflowList } from "@/components/automations/AutomationList";
import { AutomationsEducationPage } from "@/routes/automations/AutomationsEducationPage";
import IconLock from "central-icons/IconLock";
import IconChainLink1 from "central-icons/IconChainLink1";
import { AutomationsPageHeader } from "@/routes/automations/AutomationsPageHeader";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useFlagReusableWebhooksEnabled, useFlagWorkflowsEnabled, WorkflowAccessLevel } from "@/hooks/use-flag";
import { NewAutomationButton } from "@/components/automations/NewAutomationButton";
import { Button } from "@/components/flexkit/Button";
import { useNavigate } from "react-router-dom";
import type { AutomationTemplate } from "@/utils/automation-templates";
import { PermissionDenied } from "@/components/flexkit/PermissionDenied";
import { AnimatedLoadingContainer } from "@/components/ona/AnimatedLoadingContainer";
import { newWorkflowUrl, webhooksUrl } from "@/utils/automations";
import { useCanCreateAutomation, useListWorkflows } from "@/queries/automation-queries";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { withGitpodSize } from "@/assets/icons/utils";

export const AutomationsPage: FC = () => {
    useDocumentTitle("Automations");
    const { accessLevel: workflowAccessLevel, loading: isLoadingFlag } = useFlagWorkflowsEnabled();

    if (isLoadingFlag) {
        return <LoadingState />;
    }

    switch (workflowAccessLevel) {
        case WorkflowAccessLevel.Full:
            return <AutomationsPageContent />;
        case WorkflowAccessLevel.Teaser:
            return <AutomationsEducationPage />;
        case WorkflowAccessLevel.None:
        default:
            return null;
    }
};

const AutomationsPageContent: FC = () => {
    const { data: workflows, isPending: isLoadingWorkflows } = useListWorkflows();

    const { canCreateAutomations, isLoading: isLoadingCreatePermission } = useCanCreateAutomation();
    const { value: isReusableWebhooksEnabled } = useFlagReusableWebhooksEnabled();

    // User can view automations if they can create them OR if they have access to any workflows
    // The API will only return workflows you have access to, so an empty response means no workflows
    // have been shared with you.
    const hasAccessToWorkflows = workflows && workflows.length > 0;
    const canViewAutomations = canCreateAutomations || hasAccessToWorkflows;

    const navigate = useNavigate();

    const handleTemplateSelect = useCallback(
        (template: AutomationTemplate) => {
            navigate(newWorkflowUrl({ templateId: template.id }));
        },
        [navigate],
    );

    const handleManageWebhooks = useCallback(() => {
        navigate(webhooksUrl());
    }, [navigate]);

    const actions = canCreateAutomations ? (
        <div className="flex items-center gap-2">
            {isReusableWebhooksEnabled && (
                <Button
                    variant="outline"
                    LeadingIcon={withGitpodSize(IconChainLink1)}
                    onClick={handleManageWebhooks}
                    data-testid="manage-webhooks-button"
                    data-tracking-id="manage-webhooks-automations-page"
                >
                    Manage Webhooks
                </Button>
            )}
            <NewAutomationButton
                onTemplateSelect={handleTemplateSelect}
                testId="new-automation-dialog-workflows-page"
                trackingId="show-new-automation-dialog-automations-page"
            />
        </div>
    ) : undefined;

    const isLoading = isLoadingCreatePermission || isLoadingWorkflows;

    const renderContent = () => {
        if (canViewAutomations) {
            return <WorkflowList onTemplateSelect={handleTemplateSelect} />;
        }
        return (
            <PermissionDenied
                icon={<IconLock size={24} className="text-content-base" />}
                title="You don't have access to Automations"
                description={
                    <>
                        Currently you don&apos;t have permissions to view or manage automations.
                        <br />
                        Please contact your <span className="font-medium">Organization Admin</span> to request access.
                    </>
                }
                data-testid="automations-permission-denied-page"
            />
        );
    };

    return (
        <div data-testid="workflows-page" className="flex h-full grow flex-col gap-6">
            <AutomationsPageHeader actions={isLoading ? undefined : actions} />
            <AnimatedLoadingContainer isLoading={isLoading} message="Preparing automations">
                {renderContent()}
            </AnimatedLoadingContainer>
        </div>
    );
};
